import type { MetaFunction } from "@remix-run/cloudflare";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Separator } from "~/components/ui/separator";
import { AlertTriangle, Shield, FileText, Copyright, ExternalLink, Users, Gamepad2, Download } from "lucide-react";

export const meta: MetaFunction = () => {
  return [
    { title: "Disclaimer | Stellar Blade Mods" },
    { 
      name: "description", 
      content: "Important disclaimer and legal information for Stellar Blade Mods. Understand our role, responsibilities, and limitations regarding mod content and services." 
    },
    { name: "keywords", content: "disclaimer, stellar blade mods, legal notice, mod disclaimer, copyright, third party content" },
    
    // Robots directive for legal pages
    { name: "robots", content: "index,follow" },
    
    // Canonical URL
    { tagName: "link", rel: "canonical", href: "https://stellarblademod.com/disclaimer" },
    
    // Open Graph
    { property: "og:title", content: "Disclaimer | Stellar Blade Mods" },
    { property: "og:description", content: "Important disclaimer and legal information for Stellar Blade Mods website." },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://stellarblademod.com/disclaimer" },
    
    // Twitter Card
    { name: "twitter:card", content: "summary" },
    { name: "twitter:title", content: "Disclaimer | Stellar Blade Mods" },
    { name: "twitter:description", content: "Important disclaimer and legal information for Stellar Blade Mods website." },
  ];
};

export default function Disclaimer() {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <div className="relative text-white py-20 border-b border-slate-800 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-8">
              <div className="rounded-2xl bg-gradient-to-br from-orange-500/20 to-red-500/20 p-6 backdrop-blur-sm border border-white/10">
                <AlertTriangle className="h-16 w-16 text-orange-400" />
              </div>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-yellow-300">
              Disclaimer
            </h1>
            
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              Important legal information and disclaimers regarding our website, services, 
              and the content provided by our community.
            </p>
            
            <div className="flex items-center justify-center gap-6 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                <span>Legal Protection</span>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span>Important Notice</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container py-16">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8 p-4 bg-orange-900/20 border border-orange-700/30 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-400 mt-0.5" />
              <div>
                <p className="text-sm text-orange-300 font-medium mb-1">Please Read Carefully</p>
                <p className="text-sm text-slate-300">
                  This disclaimer contains important information about our services and limitations. 
                  Last updated on {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            {/* Website Role and Purpose */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Users className="h-6 w-6 text-blue-400" />
                  Our Role and Purpose
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <div className="space-y-4">
                  <div className="border-l-4 border-blue-400 pl-4">
                    <h4 className="font-semibold text-blue-300 mb-2">1. Independent Platform</h4>
                    <p className="text-sm">
                      Stellar Blade Mods does not represent any developer, nor is it the developer of 
                      Stellar Blade game or any mods featured on this website. We are an independent 
                      community platform dedicated to supporting the Stellar Blade modding community.
                    </p>
                  </div>
                  
                  <div className="border-l-4 border-green-400 pl-4">
                    <h4 className="font-semibold text-green-300 mb-2">2. Community Service</h4>
                    <p className="text-sm">
                      We provide a platform for mod creators to share their work and for users to 
                      discover and download mods. We offer custom reviews, detailed information, 
                      ratings, screenshots, and community features to enhance the modding experience.
                    </p>
                  </div>
                  
                  <div className="border-l-4 border-purple-400 pl-4">
                    <h4 className="font-semibold text-purple-300 mb-2">3. No Affiliation</h4>
                    <p className="text-sm">
                      This website is not affiliated with, endorsed by, or connected to SHIFT UP Corporation, 
                      Sony Interactive Entertainment, or any other entities involved in the creation, 
                      development, or distribution of Stellar Blade.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Trademark and Copyright */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Copyright className="h-6 w-6 text-orange-400" />
                  Trademarks and Copyrights
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <p>
                  All trademarks, registered trademarks, product names, company names, logos, and game assets 
                  appearing on this site are the property of their respective owners.
                </p>
                
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-600">
                  <h4 className="font-semibold text-slate-200 mb-3">Intellectual Property Rights</h4>
                  <ul className="list-disc list-inside space-y-2 text-sm">
                    <li>
                      <strong>Stellar Blade™</strong> is a trademark of SHIFT UP Corporation and Sony Interactive Entertainment
                    </li>
                    <li>
                      Game assets, characters, music, and original content belong to their respective copyright holders
                    </li>
                    <li>
                      Mod content is created by community members and may contain derivative works
                    </li>
                    <li>
                      We respect all intellectual property rights and respond to valid copyright claims
                    </li>
                  </ul>
                </div>
                
                <div className="bg-orange-900/20 border border-orange-700/30 p-4 rounded-lg">
                  <p className="text-sm text-orange-300">
                    <strong>Fair Use:</strong> Content on this website is provided for educational, 
                    non-commercial, and transformative purposes under fair use principles. Mods are 
                    created to enhance and extend the gaming experience for personal use.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* DMCA Compliance */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Shield className="h-6 w-6 text-red-400" />
                  DMCA Compliance
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <p>
                  Stellar Blade Mods abides by the federal Digital Millennium Copyright Act (DMCA) 
                  by responding to notices of alleged infringement that comply with the DMCA and 
                  other applicable laws.
                </p>
                
                <div className="space-y-4">
                  <h4 className="font-semibold text-white">Copyright Infringement Process</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-red-900/20 border border-red-700/30 p-4 rounded-lg">
                      <h5 className="font-semibold text-red-300 mb-2">For Copyright Holders</h5>
                      <p className="text-sm mb-2">
                        If you believe your copyrighted work has been used without permission:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>Provide detailed infringement notice</li>
                        <li>Include your contact information</li>
                        <li>Specify the copyrighted work</li>
                        <li>Identify infringing material location</li>
                      </ul>
                    </div>
                    
                    <div className="bg-blue-900/20 border border-blue-700/30 p-4 rounded-lg">
                      <h5 className="font-semibold text-blue-300 mb-2">Our Response</h5>
                      <p className="text-sm mb-2">
                        Upon receiving valid DMCA notices, we will:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>Investigate the claim promptly</li>
                        <li>Remove infringing content if verified</li>
                        <li>Notify the content uploader</li>
                        <li>Implement repeat infringer policy</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-600">
                    <p className="text-sm">
                      <strong>Contact for DMCA:</strong> If you are a copyright owner or representative 
                      and want to report infringement, please contact us at{' '}
                      <span className="text-blue-300">contact@stellarblademod.com</span> with detailed 
                      information about the alleged infringement.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Third-Party Content */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <ExternalLink className="h-6 w-6 text-purple-400" />
                  Third-Party Content and Links
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <p>
                  Our website may contain links to third-party websites, services, or content. 
                  We are not responsible for the content, privacy practices, or actions of any 
                  third-party sites or services.
                </p>
                
                <div className="space-y-4">
                  <div className="border-l-4 border-yellow-400 pl-4">
                    <h4 className="font-semibold text-yellow-300 mb-2">External Links</h4>
                    <p className="text-sm">
                      Links to external websites are provided for convenience and informational 
                      purposes only. We do not endorse or take responsibility for the content 
                      or practices of linked sites.
                    </p>
                  </div>
                  
                  <div className="border-l-4 border-purple-400 pl-4">
                    <h4 className="font-semibold text-purple-300 mb-2">User-Generated Content</h4>
                    <p className="text-sm">
                      Mods and content uploaded by users are their responsibility. We provide 
                      a platform but do not create, endorse, or guarantee the quality, safety, 
                      or legality of user-submitted content.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Mod Usage Disclaimer */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Gamepad2 className="h-6 w-6 text-green-400" />
                  Mod Usage and Safety
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <div className="bg-yellow-900/20 border border-yellow-700/30 p-4 rounded-lg mb-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-yellow-300 font-medium mb-1">Use at Your Own Risk</p>
                      <p className="text-sm text-slate-300">
                        All mods are third-party creations. Installation and use of mods may affect 
                        your game performance, save files, or system stability.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <h4 className="font-semibold text-white">Important Safety Information</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="border-l-4 border-red-400 pl-4">
                        <h5 className="font-semibold text-red-300 text-sm mb-1">Risks</h5>
                        <ul className="list-disc list-inside space-y-1 text-xs">
                          <li>Game crashes or instability</li>
                          <li>Save file corruption</li>
                          <li>Performance issues</li>
                          <li>Compatibility problems</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="border-l-4 border-green-400 pl-4">
                        <h5 className="font-semibold text-green-300 text-sm mb-1">Recommendations</h5>
                        <ul className="list-disc list-inside space-y-1 text-xs">
                          <li>Always backup save files</li>
                          <li>Read mod descriptions carefully</li>
                          <li>Check compatibility requirements</li>
                          <li>Use antivirus software</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-600">
                    <p className="text-sm">
                      <strong>No Warranty:</strong> We do not guarantee the functionality, safety, 
                      or compatibility of any mods. Users download and install mods at their own 
                      risk and responsibility.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Service Limitations */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Download className="h-6 w-6 text-gray-400" />
                  Service Limitations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <p>
                  Our website and services are provided "as is" without warranties of any kind. 
                  We make no representations or warranties regarding the accuracy, reliability, 
                  or availability of our services.
                </p>
                
                <div className="space-y-4">
                  <h4 className="font-semibold text-white">Service Disclaimers</h4>
                  
                  <div className="space-y-3">
                    <div className="border-l-4 border-gray-400 pl-4">
                      <h5 className="font-semibold text-gray-300 text-sm mb-1">Availability</h5>
                      <p className="text-xs">
                        We do not guarantee uninterrupted service availability. Maintenance, 
                        technical issues, or other factors may cause temporary service disruptions.
                      </p>
                    </div>
                    
                    <div className="border-l-4 border-gray-400 pl-4">
                      <h5 className="font-semibold text-gray-300 text-sm mb-1">Content Accuracy</h5>
                      <p className="text-xs">
                        While we strive for accuracy, we cannot guarantee that all information, 
                        descriptions, or metadata about mods is completely accurate or up-to-date.
                      </p>
                    </div>
                    
                    <div className="border-l-4 border-gray-400 pl-4">
                      <h5 className="font-semibold text-gray-300 text-sm mb-1">Data Loss</h5>
                      <p className="text-xs">
                        We are not responsible for any data loss, including but not limited to 
                        user profiles, downloaded content, or other information stored on our platform.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Advertising and Policies */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Advertising and Compliance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <p>
                  All information and advertising on this website strictly observes the terms and 
                  conditions of applicable advertising policies and content guidelines.
                </p>
                
                <div className="bg-blue-900/20 border border-blue-700/30 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-300 mb-2">Policy Compliance</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Adherence to content quality standards</li>
                    <li>Compliance with advertising regulations</li>
                    <li>Respect for user experience and safety</li>
                    <li>Regular review of content and policies</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card className="bg-slate-900/50 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Questions or Concerns</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-slate-300">
                <p>
                  If you have any questions about this disclaimer, need to report copyright 
                  infringement, or have other legal concerns, please contact us:
                </p>
                
                <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-600">
                  <div className="space-y-2 text-sm">
                    <p><strong>Email:</strong> <span className="text-blue-300">contact@stellarblademod.com</span></p>
                    <p><strong>Contact Page:</strong> <a href="/contact" className="text-blue-300 hover:text-blue-400 transition-colors">/contact</a></p>
                    <p><strong>Discord:</strong> <a href="https://discord.gg/daYR38qPB2" target="_blank" rel="noopener noreferrer" className="text-blue-300 hover:text-blue-400 transition-colors">Join our community</a></p>
                  </div>
                </div>
                
                <div className="bg-green-900/20 border border-green-700/30 p-4 rounded-lg">
                  <p className="text-sm text-green-300">
                    We are committed to addressing legitimate concerns promptly and maintaining 
                    a respectful environment for the Stellar Blade modding community.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}